import type { ObjectiveStrategy } from '@moment/horizon-data-access/proposal'

import isNil from 'lodash/isNil'

import { formatDecimal, PLACEHOLDER } from '@/utils/presentation/formatting'

export { CURRENCY_SYMBOLS } from '@/shared/constants/international'

/**
 * Present-tense label for a sleeve's non-default objective strategy (shown in
 * the sleeve list info icon). Returns null for the default/revenue strategy.
 */
export function getObjectiveStrategyLabel(
  strategy: ObjectiveStrategy | undefined,
): string | null {
  if (strategy === 'maximize_cashflows') return 'Maximizing cashflows'
  if (strategy === 'minimize_deployed_cash') return 'Minimizing deployed cash'
  return null
}

/** Maximize Cashflows is the one objective whose targets carry no dollar floor. */
export function isMaximizeCashflows(
  strategy: ObjectiveStrategy | undefined,
): boolean {
  return strategy === 'maximize_cashflows'
}

/**
 * Formatting constants for proposal-related views.
 * Ensures consistent precision across all proposal components.
 */
export const PROPOSAL_FORMATTING = {
  /** Decimal places for price values (e.g., 100.1234) */
  PRICE_DECIMALS: 4,
  /** Decimal places for YTW/yield values (e.g., 5.123%) */
  YTW_DECIMALS: 3,
  /** Decimal places for aggregate stat values (avg coupon/YTW/duration/maturity) */
  STAT_DECIMALS: 2,
} as const

/** Formats an aggregate "years" stat to STAT_DECIMALS precision, e.g. `5.30 yrs`. */
export function formatYears(value: number | undefined): string {
  if (value == null) return PLACEHOLDER
  return `${formatDecimal(value, PROPOSAL_FORMATTING.STAT_DECIMALS)} yrs`
}

/**
 * Formats a number without a currency symbol.
 * Drop-in replacement for formatMoney where currency symbols should not appear.
 */
export function formatMoneyNoCurrency(
  price: number | null,
  options: {
    numDigits?: number
    useZeroPlaceholder?: boolean
    addTrailingZeros?: boolean
  } = {},
): string {
  const {
    numDigits = 2,
    useZeroPlaceholder = false,
    addTrailingZeros = false,
  } = options

  if (isNil(price) || (typeof price === 'number' && Number.isNaN(price))) {
    return PLACEHOLDER
  }

  if (price === 0 && useZeroPlaceholder) {
    return PLACEHOLDER
  }

  const formatter = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: addTrailingZeros ? numDigits : undefined,
    maximumFractionDigits: numDigits,
  })

  return formatter.format(Number(price))
}
